import { defineStore } from 'pinia'

import {
	fetchApiNewCertificateList,
	fetchApiPatchMonitoring
} from '~/portal/api'
import { useSnackbar } from '~/portal/composables'
import type {
	CertificateItem,
	CertificateListResponse,
	PatchMonitoringPayload
} from '~/portal/stores'

const showGeneralError = () => {
	const snack = useSnackbar()

	snack.showSnackbar('Ein unerwarteter Fehler ist aufgetreten.', 'cp-base-red')
}

export const useNewCertificateStore = defineStore('certificateStore', {
	state: () => ({
		totalItems: 0,
		totalPages: 0,
		currentPage: 1,
		isPagination: false,
		certificateList: [] as CertificateItem[],
		loading: false,
		ordering: '-plant_certification_date' as string
	}),
	actions: {
		resetCertificateState() {
			this.currentPage = 1
			this.certificateList = []
			this.isPagination = false
			this.totalItems = 0
			this.totalPages = 0
		},

		async fetchCertificateList(payload: {
			token: string
			page?: number
			ordering?: string
		}) {
			const page = payload.page ?? this.currentPage
			const ordering = payload.ordering ?? this.ordering

			this.currentPage = page
			this.ordering = ordering
			this.loading = true

			try {
				const response = await fetchApiNewCertificateList({
					token: payload.token,
					params: { page, ordering }
				})

				const data = response?.data as CertificateListResponse | undefined
				if (!data) return

				this.totalItems = data.count
				this.totalPages = Math.ceil(this.totalItems / 10)

				if (page > 1) {
					this.certificateList.push(
						...(data.results as unknown as CertificateItem[])
					)
				} else {
					this.certificateList = data.results
				}

				this.currentPage = page
				this.isPagination = Boolean(data.next)
			} catch (e) {
				showGeneralError()
				throw e
			} finally {
				this.loading = false
			}
		},

		async loadNextPage(token: string, ordering: string) {
			if (!this.isPagination || this.loading) return
			return this.fetchCertificateList({
				token,
				page: this.currentPage + 1,
				ordering
			})
		},

		async updateMonitoringStatus({
			token,
			index,
			projectId,
			monitoring_status
		}: {
			token: string
			index: number
			projectId: number
			monitoring_status: 'COMPLETED' | 'VALID' | 'EXTENDED' | 'INVALID'
		}) {
			const snack = useSnackbar()
			snack.showSnackbar('Status aktualisiert')

			this.loading = true
			try {
				await fetchApiPatchMonitoring({
					token,
					projectId,
					payload: { monitoring_status }
				})
				const current = this.certificateList[index]
				if (!current) return
				this.certificateList.splice(index, 1, {
					...current,
					monitoring_status
				})
			} catch (e) {
				showGeneralError()
				throw e
			} finally {
				this.loading = false
			}
		},

		async updateDatesOrComment({
			token,
			index,
			projectId,
			payload
		}: {
			token: string
			index: number
			projectId: number
			payload: PatchMonitoringPayload & { policies?: string | null }
		}) {
			const snack = useSnackbar()
			const row = this.certificateList[index]
			if (!row) return

			const existingSettings = row.certificate_settings ?? {}
			const mergedSettings = payload.certificate_settings
				? { ...existingSettings, ...payload.certificate_settings }
				: undefined

			const finalPayload: Record<string, any> = {
				...(payload.public_comment !== undefined && {
					public_comment: payload.public_comment
				}),
				...(mergedSettings && { certificate_settings: mergedSettings }),
				...(payload.policies !== undefined && { policies: payload.policies })
			}

			this.loading = true
			try {
				await fetchApiPatchMonitoring({
					token,
					projectId,
					payload: finalPayload
				})
				snack.showSnackbar('Änderungen gespeichert')

				const updatedRow = {
					...row,
					public_comment: payload.public_comment ?? row.public_comment,
					certificate_settings: mergedSettings ?? existingSettings,
					...(payload.policies !== undefined
						? { policies: payload.policies }
						: {})
				}
				this.certificateList.splice(index, 1, updatedRow)

				const origDate = existingSettings.plant_certification_date ?? null
				const newDate = mergedSettings?.plant_certification_date ?? null
				if (newDate !== origDate) {
					await this.fetchCertificateList({
						token,
						page: 1,
						ordering: this.ordering
					})
				}
			} catch (e) {
				showGeneralError()
				throw e
			} finally {
				this.loading = false
			}
		}
	}
})
